import { openDB, DBSchema, IDBPDatabase } from 'idb';
import { projectApi } from './projectApi';

interface BongaiDB extends DBSchema {
  canvasCache: {
    key: string;
    value: {
      projectId: string;
      version: number;
      nodes: any[];
      edges: any[];
      groups: any[];
      viewport: { x: number; y: number; zoom: number };
      updatedAt: number;
    };
  };
  agentCache: {
    key: string;
    value: {
      sessionId: string;
      projectId: string;
      version: number;
      messages: any[];
      currentModelId: string;
      updatedAt: number;
    };
  };
  syncQueue: {
    key: number;
    value: {
      id?: number;
      type: 'canvas' | 'agent';
      projectId: string;
      operation: 'save' | 'delete';
      data: any;
      version: number;
      timestamp: number;
    };
  };
  appData: {
    key: string;
    value: any;
  };
}

class UnifiedStorage {
  private db: IDBPDatabase<BongaiDB> | null = null;
  private syncDebounceTimer: number | null = null;
  private syncRetryTimer: number | null = null;
  private readonly DEBOUNCE_MS = 500;
  private readonly RETRY_MS = 3000;
  private initialized = false;
  private localVersions: Map<string, number> = new Map();
  private isOnline: boolean = typeof navigator !== 'undefined' ? navigator.onLine : true;
  private onlineRetryTimer: number | null = null;
  private syncing = false;
  private pendingSync = false;
  // ── localStorage 配额治理 ──
  // localStorage 单域仅 ~5MB，多个备份 key（画布/会话/项目缓存/紧急备份）累积
  // 会挤爆配额，导致任何写入都 QuotaExceededError。治理三层：
  // 1) 预算门：单 key 备份超预算直接跳过 localStorage 层（IndexedDB 为主存储）
  // 2) 同 key 自愈：覆盖写失败时先清同 key 旧值再重试
  // 3) 跨 key 淘汰：配额仍不足时，按"纯缓存优先、IDB 兜底备份按 updatedAt
  //    从旧到新"淘汰其他 bongai_* 备份，为当前写入腾位
  private readonly BACKUP_MAX_BYTES = 1.5 * 1024 * 1024;
  private backupSkippedKeys = new Set<string>();
  private writeFailedKeys = new Set<string>();
  // 淘汰告警去重：同一受害者 key 会话内只告警一次，避免每次保存刷屏
  private evictedVictimKeys = new Set<string>();
  // 配额压力标记（会话内粘性）：发生过跨 key 淘汰即置位，说明配额常态吃紧
  // （同一份画布数据在 project_cache/canvas/sync_queue_backup 存了 3 份镜像，
  // 大项目下必然互踢）。置位后冗余镜像不再写 localStorage，斩断互踢循环。
  // 读路径不受影响：emergency/sync_queue 仅在 IDB 不可用时兜底，
  // 此时 project_cache/canvas 镜像仍持有同一数据
  private quotaPressure = false;
  // 配额压力下不再镜像的冗余 key（IndexedDB 为主存储，且数据已有其他镜像）
  private static readonly PRESSURE_SKIPPED = [
    /^bongai_emergency_/,
    /^bongai_sync_queue_backup$/,
  ];
  // 永不淘汰的 bongai_* key（认证/版本标记/跨 tab 通信等非备份数据）
  private static readonly EVICTION_PROTECTED = [
    /^bongai_token$/,
    /^bongai_data_version$/,
    /^bongai_last_save_/,
    /^bongai_agent_broadcast$/,
  ];

  async init(): Promise<boolean> {
    if (this.initialized) return true;
    try {
      this.db = await openDB<BongaiDB>('bongai-unified', 2, {
        upgrade(db, oldVersion) {
          if (oldVersion < 1) {
            db.createObjectStore('canvasCache', { keyPath: 'projectId' });
            db.createObjectStore('agentCache', { keyPath: 'sessionId' });
            db.createObjectStore('syncQueue', { keyPath: 'id', autoIncrement: true });
          }
          if (oldVersion < 2) {
            db.createObjectStore('appData');
          }
        }
      });
      this.initialized = true;
      await this._migratePhase1Data();
      this._setupOnlineListener();
      return true;
    } catch (e) {
      console.warn('[UnifiedStorage] IndexedDB init failed, falling back to localStorage:', e);
      this.initialized = false;
      return false;
    }
  }

  isReady(): boolean {
    return this.initialized && this.db !== null;
  }

  private _setupOnlineListener(): void {
    if (typeof window === 'undefined') return;
    window.addEventListener('online', () => {
      this.isOnline = true;
      this.processSyncQueue();
    });
    window.addEventListener('offline', () => {
      this.isOnline = false;
    });
  }

  private async _migratePhase1Data(): Promise<void> {
    if (!this.db) return;
    try {
      const oldKeys = await this._getOldStorageKeys();
      for (const key of oldKeys) {
        const oldData = await this._getOldStorageData(key);
        if (!oldData) continue;

        if (key.startsWith('agent_messages_')) {
          const sessionId = key.replace('agent_messages_', '');
          if (Array.isArray(oldData)) {
            await this.db.put('agentCache', {
              sessionId,
              projectId: '',
              version: 0,
              messages: oldData,
              currentModelId: 'grsai-gemini-3.1-flash-lite',
              updatedAt: Date.now(),
            });
          }
        } else {
          await this.db.put('appData', oldData, key);
        }
        await this._removeOldStorageData(key);
      }
    } catch (e) {
      console.warn('[UnifiedStorage] Phase 1 data migration failed:', e);
    }
  }

  private async _getOldStorageKeys(): Promise<string[]> {
    try {
      const oldDB = await openDB('bong_ai_db', 1);
      const keys = await oldDB.getAllKeys('app_data');
      oldDB.close();
      return keys as string[];
    } catch {
      return [];
    }
  }

  private async _getOldStorageData(key: string): Promise<any> {
    try {
      const oldDB = await openDB('bong_ai_db', 1);
      const data = await oldDB.get('app_data', key);
      oldDB.close();
      return data;
    } catch {
      return null;
    }
  }

  private async _removeOldStorageData(key: string): Promise<void> {
    try {
      const oldDB = await openDB('bong_ai_db', 1);
      await oldDB.delete('app_data', key);
      oldDB.close();
    } catch {
    }
  }

  private _stripNodeDataUrls(nodes: any[]): any[] {
    // Only strip base64 data URLs (data:...) to reduce storage size.
    // Keep URL references like /assets/... so that media is preserved.
    // 媒体字段（image/videoUri 等）即使是 data URL 也必须保留，否则刷新后图片永久丢失
    const dataUrlOnlyKeys = new Set(['dataUrl', 'thumbnailDataUrl']);
    const mediaKeys = new Set(['image', 'images', 'videoUri', 'videoUris', 'audioUri', 'panoramaOutputUrl']);
    return nodes.map(node => {
      const data = node.data;
      if (!data) return node;
      const stripped: any = {};
      let changed = false;
      for (const key of Object.keys(data)) {
        if (dataUrlOnlyKeys.has(key)) {
          changed = true;
          continue;
        }
        // 保留媒体字段，即使它们是 data URL（防止刷新后数据丢失）
        if (mediaKeys.has(key)) {
          stripped[key] = data[key];
          continue;
        }
        // Strip only if the value is a base64 data URL (starts with "data:")
        const val = data[key];
        if (typeof val === 'string' && val.startsWith('data:')) {
          changed = true;
          continue;
        }
        // Strip base64 entries inside arrays
        if (Array.isArray(val)) {
          const filtered = val.filter((v: any) => !(typeof v === 'string' && v.startsWith('data:')));
          if (filtered.length !== val.length) {
            changed = true;
            stripped[key] = filtered;
            continue;
          }
        }
        stripped[key] = data[key];
      }
      return changed ? { ...node, data: stripped } : node;
    });
  }

  async saveCanvas(projectId: string, data: {
    nodes: any[]; edges: any[]; groups: any[];
    viewport: { x: number; y: number; zoom: number; updatedAt?: number };
  }): Promise<number> {
    await this.init();

    const strippedNodes = this._stripNodeDataUrls(data.nodes);

    const cacheKey = `canvas_${projectId}`;
    let currentVersion = this.localVersions.get(cacheKey) || 0;

    if (currentVersion === 0 && this.isReady()) {
      const cached = await this.db!.get('canvasCache', projectId);
      if (cached) currentVersion = cached.version;
    }

    const newVersion = currentVersion + 1;
    this.localVersions.set(cacheKey, newVersion);

    // 统一补 viewport.updatedAt：远端多端合并（mergeCanvasStates）按时间戳取较新视口
    const strippedData = {
      ...data,
      nodes: strippedNodes,
      viewport: { ...data.viewport, updatedAt: data.viewport.updatedAt ?? Date.now() },
    };

    if (this.isReady()) {
      await this.db!.put('canvasCache', {
        projectId, version: newVersion, ...strippedData, updatedAt: Date.now(),
      });
    }

    this._backupToLocalStorage(`bongai_canvas_${projectId}`, {
      projectId, version: newVersion, ...strippedData, updatedAt: Date.now()
    });

    this.enqueueSync('canvas', projectId, strippedData, newVersion);
    return newVersion;
  }

  async getCanvas(projectId: string) {
    await this.init();
    if (this.isReady()) {
      const cached = await this.db!.get('canvasCache', projectId);
      if (cached) return cached;
    }
    try {
      const fallback = localStorage.getItem(`bongai_canvas_${projectId}`);
      if (fallback) return JSON.parse(fallback);
    } catch (e) {
      console.warn('[UnifiedStorage] localStorage fallback read failed:', e);
    }
    return null;
  }

  async saveAgent(sessionId: string, data: {
    messages: any[]; currentModelId: string; projectId: string;
  }): Promise<number> {
    await this.init();

    const cacheKey = `agent_${sessionId}`;
    let currentVersion = this.localVersions.get(cacheKey) || 0;
    // 🔴 方案B（阶段三 FE-4）：原"保留流式快照字段"逻辑已随 G0 快照体系删除
    // （切回富格式恢复统一走 DB task_snapshot 真源）。旧记录可能残留 snapshot
    // 字段，此处全量覆盖写自然清除，无需特殊处理。
    if (currentVersion === 0) {
      const cached = this.isReady() ? await this.db!.get('agentCache', sessionId) : null;
      if (cached) currentVersion = cached.version;
    }

    const newVersion = currentVersion + 1;
    this.localVersions.set(cacheKey, newVersion);

    const record = {
      sessionId, projectId: data.projectId, version: newVersion,
      messages: data.messages, currentModelId: data.currentModelId,
      updatedAt: Date.now(),
    };

    if (this.isReady()) {
      await this.db!.put('agentCache', record);
    }

    this._backupToLocalStorage(`bongai_agent_${sessionId}`, record);

    // 🔴 方案B（阶段三 BE-7）：移除 enqueueSync('agent', ...) 后端同步通道。
    // 原通道经 syncQueue -> /api/state（state_snapshots）全量序列化上传，
    // 但恢复链路零读取（agent_messages API + 本地 agentCache 即真源），且
    // 写入绕过 agent_messages 写保护（BE-2a），构成旁路第三真源。本地
    // IndexedDB/localStorage 写入保留，离线兜底语义不变；syncQueue 存量
    // agent 条目随正常消费清空；canvas 类型留画布专项另行评估。
    return newVersion;
  }

  async getAgent(sessionId: string) {
    await this.init();
    if (this.isReady()) {
      const cached = await this.db!.get('agentCache', sessionId);
      if (cached) return cached;
    }
    try {
      const fallback = localStorage.getItem(`bongai_agent_${sessionId}`);
      if (fallback) return JSON.parse(fallback);
    } catch (e) {
      console.warn('[UnifiedStorage] localStorage fallback read failed:', e);
    }
    return null;
  }

  private async enqueueSync(
    type: 'canvas' | 'agent', id: string, data: any, version: number
  ): Promise<void> {
    if (!this.isReady()) {
      this.debouncedSync();
      return;
    }

    // 入队前去重：同 type+id 的旧 item 已被本次全量数据覆盖
    // （processSyncQueue 本就按最新 version 收敛，旧 item 是冗余）。
    // 不去重时离线/同步失败期间队列随每次防抖保存无限膨胀，
    // localStorage 镜像随之超出配额（QuotaExceededError 根因）
    try {
      const existing = await this.db!.getAll('syncQueue');
      for (const item of existing) {
        if (item.type === type && item.projectId === id && item.id !== undefined) {
          await this.db!.delete('syncQueue', item.id);
        }
      }
    } catch (e) {
      console.warn('[UnifiedStorage] syncQueue dedup failed:', e);
    }

    await this.db!.add('syncQueue', {
      type, projectId: id, operation: 'save', data, version,
      timestamp: Date.now(),
    });

    await this._backupSyncQueue();
    this.debouncedSync();
  }

  // syncQueue 的 localStorage 镜像统一出口（enqueueSync 与 processSyncQueue 共用）：
  // 队列为空时清 key，非空走统一预算门
  private async _backupSyncQueue(): Promise<void> {
    if (!this.isReady()) return;
    try {
      const queue = await this.db!.getAll('syncQueue');
      if (queue.length === 0) {
        localStorage.removeItem('bongai_sync_queue_backup');
        return;
      }
      this._backupToLocalStorage('bongai_sync_queue_backup', queue);
    } catch (e) {
      console.warn('[UnifiedStorage] syncQueue backup failed:', e);
    }
  }

  // 备份写统一入口（预算门）：序列化超预算的备份跳过 localStorage 层并移除
  // 旧值（防止陈旧超大备份永久占坑），告警每个 key 只发一次；预算内走安全写。
  // 配额压力下额外降级：冗余镜像（emergency_/sync_queue_backup）直接跳过，
  // 防止大项目多镜像互踢循环（每次保存互相淘汰对方再重写）
  private _backupToLocalStorage(key: string, payload: unknown): boolean {
    if (this.quotaPressure && UnifiedStorage.PRESSURE_SKIPPED.some(re => re.test(key))) {
      localStorage.removeItem(key);
      if (!this.backupSkippedKeys.has(key)) {
        this.backupSkippedKeys.add(key);
        console.warn(
          `[UnifiedStorage] backup for "${key}" skipped under localStorage quota pressure ` +
          `(IndexedDB remains primary storage)`
        );
      }
      return false;
    }
    const serialized = JSON.stringify(payload);
    if (serialized.length > this.BACKUP_MAX_BYTES) {
      localStorage.removeItem(key);
      if (!this.backupSkippedKeys.has(key)) {
        this.backupSkippedKeys.add(key);
        console.warn(
          `[UnifiedStorage] backup for "${key}" skipped: ` +
          `${serialized.length} bytes exceeds budget ${this.BACKUP_MAX_BYTES} bytes` +
          ` (IndexedDB remains primary storage)`
        );
      }
      return false;
    }
    const ok = this._setLocalStorageSafely(key, serialized);
    if (ok) this.backupSkippedKeys.delete(key);
    return ok;
  }

  // localStorage 安全写：配额满时先移除同 key 旧值（本就要覆盖写）再重试；
  // 仍失败说明配额被其他 bongai_* 备份占用，逐个淘汰后重试直到成功或无候选。
  // 最终失败每个 key 只告警一次（避免每次保存刷屏）
  private _setLocalStorageSafely(key: string, value: string): boolean {
    try {
      localStorage.setItem(key, value);
      return true;
    } catch { /* 落到自愈流程 */ }
    try {
      localStorage.removeItem(key);
      localStorage.setItem(key, value);
      return true;
    } catch { /* 落到跨 key 淘汰 */ }
    while (true) {
      const victim = this._nextEvictionCandidate(key);
      if (!victim) break;
      localStorage.removeItem(victim);
      // 需要跨 key 淘汰说明配额常态吃紧：置压力标记，
      // 后续冗余镜像（emergency_/sync_queue_backup）不再写入
      this.quotaPressure = true;
      // 同一受害者 key 会话内只告警一次，避免互踢循环刷屏
      if (!this.evictedVictimKeys.has(victim)) {
        this.evictedVictimKeys.add(victim);
        console.warn(`[UnifiedStorage] evicted stale backup "${victim}" to free localStorage quota`);
      }
      try {
        localStorage.setItem(key, value);
        this.writeFailedKeys.delete(key);
        return true;
      } catch { /* 继续淘汰下一个候选 */ }
    }
    if (!this.writeFailedKeys.has(key)) {
      this.writeFailedKeys.add(key);
      console.warn(`[UnifiedStorage] localStorage write failed for key "${key}" (quota exhausted)`);
    }
    return false;
  }

  // 选择下一个淘汰对象：其他 bongai_* 备份 key 中，
  // 纯缓存（emergency_/project_cache_，可从后端重建）优先淘汰；
  // canvas_/agent_ 等 IDB 兜底备份按 payload.updatedAt 从旧到新淘汰
  private _nextEvictionCandidate(excludeKey: string): string | null {
    const candidates: Array<{ key: string; priority: number; updatedAt: number }> = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k || !k.startsWith('bongai_') || k === excludeKey) continue;
      if (UnifiedStorage.EVICTION_PROTECTED.some(re => re.test(k))) continue;
      const raw = localStorage.getItem(k);
      if (raw === null) continue;
      const isCache = k.startsWith('bongai_emergency_') || k.startsWith('bongai_project_cache_');
      let updatedAt = 0;
      if (!isCache) {
        try {
          const parsed = JSON.parse(raw);
          if (typeof parsed?.updatedAt === 'number') updatedAt = parsed.updatedAt;
        } catch { /* 解析失败视为最旧 */ }
      }
      candidates.push({ key: k, priority: isCache ? 0 : 1, updatedAt });
    }
    if (candidates.length === 0) return null;
    candidates.sort((a, b) =>
      a.priority - b.priority || a.updatedAt - b.updatedAt || (a.key < b.key ? -1 : 1)
    );
    return candidates[0].key;
  }

  private debouncedSync() {
    if (this.syncDebounceTimer) clearTimeout(this.syncDebounceTimer);
    this.syncDebounceTimer = window.setTimeout(() => {
      this.processSyncQueue();
    }, this.DEBOUNCE_MS);
  }

  async processSyncQueue(): Promise<void> {
    if (!this.isReady()) return;
    if (!this.isOnline) return;
    if (this.syncing) {
      this.pendingSync = true;
      return;
    }
    this.syncing = true;

    try {
      while (true) {
        const queue = await this.db!.getAll('syncQueue');
        if (queue.length === 0) break;

        // P3d（BE-7 收尾）：清除存量 agent 类型同步条目。前端已不再入队 agent
        // 类型（saveAgentState 移除 enqueueSync('agent')），但升级前残留的队列
        // 条目若继续 POST /api/state/save 会被收紧后的后端（state_type 仅接受
        // canvas）以 422 拒绝，触发 3s 无限重试。此处直接删除，不同步。
        for (const item of queue) {
          if (item.type === 'agent') {
            await this.db!.delete('syncQueue', item.id!);
          }
        }
        const activeQueue = queue.filter(item => item.type !== 'agent');
        if (activeQueue.length === 0) {
          await this._backupSyncQueue();
          break;
        }

        const latestByProject = new Map<string, BongaiDB['syncQueue']['value']>();
        for (const item of activeQueue) {
          const key = `${item.type}_${item.projectId}`;
          const existing = latestByProject.get(key);
          if (!existing || item.version > existing.version) {
            latestByProject.set(key, item);
          }
        }

        // 跟踪已成功同步的 key：只删除这些 key 对应的所有 item（含旧 version）
        // 失败或未处理的 key 对应的 item 必须保留在队列中,否则 "will retry" 永远不会真正重试
        const syncedKeys = new Set<string>();
        let hadFailure = false;
        for (const item of latestByProject.values()) {
          const key = `${item.type}_${item.projectId}`;
          try {
            const newVersion = await this.syncToBackend(item);
            if (newVersion !== null) {
              await this._updateLocalVersion(item.type, item.projectId, newVersion);
            }
            syncedKeys.add(key);
          } catch (error) {
            console.warn('Sync failed, will retry:', error);
            hadFailure = true;
            break;
          }
        }

        for (const item of queue) {
          const key = `${item.type}_${item.projectId}`;
          if (syncedKeys.has(key)) {
            await this.db!.delete('syncQueue', item.id!);
          }
        }

        // 有失败则跳出 while,3 秒后自动重试（队列中仍保留失败 item）
        // 避免用户无新操作时失败任务永久滞留；持续失败时每次重试会再次调度,直到成功或网络恢复
        if (hadFailure) {
          if (this.syncRetryTimer) clearTimeout(this.syncRetryTimer);
          this.syncRetryTimer = window.setTimeout(() => {
            this.syncRetryTimer = null;
            this.processSyncQueue();
          }, this.RETRY_MS);
          break;
        }

        if (!this.pendingSync) break;
        this.pendingSync = false;
      }
    } finally {
      this.syncing = false;
      this.pendingSync = false;
    }

    // 队列收敛后同步更新 localStorage 镜像（含清空场景）
    await this._backupSyncQueue();
  }

  private async _updateLocalVersion(type: 'canvas' | 'agent', id: string, version: number): Promise<void> {
    const cacheKey = `${type}_${id}`;
    this.localVersions.set(cacheKey, version);
    if (!this.db) return;
    if (type === 'canvas') {
      const cached = await this.db.get('canvasCache', id);
      if (cached) { cached.version = version; await this.db.put('canvasCache', cached); }
    } else {
      const cached = await this.db.get('agentCache', id);
      if (cached) { cached.version = version; await this.db.put('agentCache', cached); }
    }
  }

  private async syncToBackend(item: BongaiDB['syncQueue']['value']): Promise<number | null> {
    const remote = await fetch(`/api/state/${item.projectId}/${item.type}`).then(r => r.ok ? r.json() : null);
    const remoteVersion = remote?.version ?? -1;

    const merged = remote?.data
      ? (item.type === 'canvas'
        ? this.mergeCanvasStates(remote.data, item.data)
        : this.mergeAgentStates(remote.data, item.data))
      : item.data;

    const response = await fetch('/api/state/save', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        project_id: item.projectId,
        state_type: item.type,
        data: merged,
        expected_version: remoteVersion,
      }),
    });

    if (!response.ok) {
      throw new Error(`Sync failed: ${response.status}`);
    }

    const result = await response.json();
    return result.version;
  }

  mergeCanvasStates(remote: any, local: any): any {
    const nodeMap = new Map();
    [...(remote?.nodes || []), ...(local?.nodes || [])].forEach(node => {
      const existing = nodeMap.get(node.id);
      if (!existing || (node.updatedAt || 0) > (existing.updatedAt || 0)) {
        nodeMap.set(node.id, node);
      }
    });

    const edgeMap = new Map();
    [...(remote?.edges || []), ...(local?.edges || [])].forEach(edge => {
      const edgeId = edge.id || `${edge.source}-${edge.target}`;
      const existing = edgeMap.get(edgeId);
      if (!existing || (edge.updatedAt || 0) > (existing.updatedAt || 0)) {
        edgeMap.set(edgeId, edge);
      }
    });

    const remoteViewportTime = remote?.viewport?.updatedAt || 0;
    const localViewportTime = local?.viewport?.updatedAt || 0;
    const mergedViewport = localViewportTime >= remoteViewportTime
      ? local?.viewport : remote?.viewport;

    return {
      nodes: this._stripNodeDataUrls(Array.from(nodeMap.values())),
      edges: Array.from(edgeMap.values()),
      groups: remote?.groups || local?.groups || [],
      viewport: mergedViewport || { x: 0, y: 0, zoom: 1 },
    };
  }

  mergeAgentStates(remote: any, local: any): any {
    const messageMap = new Map();
    [...(remote?.messages || []), ...(local?.messages || [])].forEach(msg => {
      const existing = messageMap.get(msg.id);
      if (!existing || (msg.updatedAt || msg.createdAt || 0) > (existing.updatedAt || existing.createdAt || 0)) {
        messageMap.set(msg.id, msg);
      }
    });
    return {
      messages: Array.from(messageMap.values()).sort(
        (a, b) => (a.sequence || 0) - (b.sequence || 0)
      ),
      currentModelId: local?.currentModelId || remote?.currentModelId || 'grsai-gemini-3.1-flash-lite',
    };
  }

  async restoreSyncQueueFromBackup(): Promise<void> {
    try {
      const backup = localStorage.getItem('bongai_sync_queue_backup');
      if (!backup || !this.isReady()) return;
      const items = JSON.parse(backup);
      for (const item of items) {
        if (!item.id) continue;
        const existing = await this.db!.get('syncQueue', item.id);
        if (!existing) {
          await this.db!.add('syncQueue', item);
        }
      }
    } catch (e) {
      console.warn('[UnifiedStorage] Failed to restore sync queue from backup:', e);
    }
  }

  private autoSaveTimer: ReturnType<typeof setInterval> | null = null;
  private isDirty: boolean = false;
  private saving: boolean = false;
  private readonly autoSaveInterval: number = 60000;

  markDirty(): void {
    this.isDirty = true;
  }

  startAutoSave(
    projectId: string | null,
    getData: () => { nodes: any[]; connections: any[]; groups: any[]; viewport?: { x: number; y: number; zoom: number } },
    getThumbnail: () => string
  ): void {
    this.stopAutoSave();
    this.autoSaveTimer = setInterval(async () => {
      if (this.saving || !projectId) return;
      const data = getData();
      if (data.nodes.length === 0) return;
      this.saving = true;
      try {
        this.saveToLocalStorage(`bongai_project_cache_${projectId}`, data);
        await this.saveToBackend(projectId, data, getThumbnail());
        await this.saveCanvas(projectId, {
          nodes: data.nodes,
          edges: data.connections,
          groups: data.groups,
          viewport: data.viewport || { x: 0, y: 0, zoom: 1 },
        });
        this.isDirty = false;
      } catch (e) {
        console.error('[UnifiedStorage] Auto-save failed:', e);
      } finally {
        this.saving = false;
      }
    }, this.autoSaveInterval);
  }

  stopAutoSave(): void {
    if (this.autoSaveTimer) {
      clearInterval(this.autoSaveTimer);
      this.autoSaveTimer = null;
    }
  }

  async flush(
    projectId: string | null,
    data: { nodes: any[]; connections: any[]; groups: any[]; viewport?: { x: number; y: number; zoom: number } },
    thumbnail?: string
  ): Promise<void> {
    // beforeunload 场景：async 链在 IndexedDB 宏任务处可能被页面卸载中断，
    // 先同步写 localStorage（含 viewport），确保刷新后视口可恢复
    if (projectId) {
      this.saveToLocalStorage(`bongai_project_cache_${projectId}`, data);
    }
    await this.saveToIndexedDB('nodes', data.nodes);
    await this.saveToIndexedDB('connections', data.connections);
    await this.saveToIndexedDB('groups', data.groups);
    await this.saveEmergencyBackup(data.nodes, data.connections, data.groups);
    if (projectId) {
      await this.saveCanvas(projectId, {
        nodes: data.nodes,
        edges: data.connections,
        groups: data.groups,
        viewport: data.viewport || { x: 0, y: 0, zoom: 1 },
      });
    }
  }

  async saveToIndexedDB(key: string, data: any): Promise<void> {
    await this.init();
    if (this.isReady()) {
      await this.db!.put('appData', data, key);
    }
  }

  async loadFromIndexedDB<T>(key: string): Promise<T | undefined> {
    await this.init();
    if (this.isReady()) {
      return await this.db!.get('appData', key) as T | undefined;
    }
    return undefined;
  }

  saveToLocalStorage(key: string, data: any): void {
    // 统一走预算门 + 安全写（当前仅用于 bongai_project_cache_* 纯缓存）
    this._backupToLocalStorage(key, data);
  }

  loadFromLocalStorage<T>(key: string): T | null {
    try {
      const raw = localStorage.getItem(key);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  async cacheAsset(url: string): Promise<void> {
    if (!('caches' in window)) return;
    try {
      const cache = await caches.open('bong-ai-media-v1');
      const response = await cache.match(url);
      if (!response) {
        const fetchResponse = await fetch(url);
        if (fetchResponse.ok) {
          await cache.put(url, fetchResponse);
        }
      }
    } catch {}
  }

  async getCachedAsset(url: string): Promise<Response | undefined> {
    if (!('caches' in window)) return undefined;
    try {
      const cache = await caches.open('bong-ai-media-v1');
      return await cache.match(url);
    } catch {
      return undefined;
    }
  }

  async clearAssetCache(): Promise<void> {
    if (!('caches' in window)) return;
    try {
      await caches.delete('bong-ai-media-v1');
    } catch {}
  }

  async saveEmergencyBackup(nodes: any[], connections: any[], groups: any[]): Promise<void> {
    // 紧急备份走统一预算门：单 key 超预算跳过 localStorage 层，
    // 逐 key 独立写入：单 key 失败不影响其余 key
    this._backupToLocalStorage('bongai_emergency_nodes', nodes);
    this._backupToLocalStorage('bongai_emergency_connections', connections);
    this._backupToLocalStorage('bongai_emergency_groups', groups);
  }

  async loadEmergencyBackup<T = any>(): Promise<{ nodes: T[] | null; connections: T[] | null; groups: T[] | null }> {
    try {
      const nodes = this.loadFromLocalStorage<T[]>('bongai_emergency_nodes');
      const connections = this.loadFromLocalStorage<T[]>('bongai_emergency_connections');
      const groups = this.loadFromLocalStorage<T[]>('bongai_emergency_groups');
      return { nodes, connections, groups };
    } catch {
      return { nodes: null, connections: null, groups: null };
    }
  }

  async saveProjectCache(projectId: string, data: { nodes: any[]; connections: any[]; groups: any[]; viewport?: { x: number; y: number; zoom: number } }): Promise<void> {
    this.saveToLocalStorage(`bongai_project_cache_${projectId}`, data);
  }

  async loadProjectCache<T = any>(projectId: string): Promise<{ nodes: T[]; connections: T[]; groups: T[] } | null> {
    return this.loadFromLocalStorage<{ nodes: T[]; connections: T[]; groups: T[] }>(`bongai_project_cache_${projectId}`);
  }

  async saveToBackend(projectId: string, data: { nodes: any[]; connections: any[]; groups: any[] }, thumbnail?: string): Promise<void> {
    if (!projectId) return;
    try {
      const updateData: any = { workflow_data: data };
      if (thumbnail !== undefined && thumbnail !== '') {
        updateData.thumbnail = thumbnail;
      }
      await projectApi.updateProject(projectId, updateData);
      // Keep projectStore.currentProject.workflow_data in sync with ground-truth node state
      try {
        const { useProjectStore } = await import('../stores/projectStore');
        const ps = useProjectStore.getState();
        if (ps.currentProjectId === projectId && ps.currentProject) {
          ps.updateWorkflowData(data.nodes, data.connections, data.groups);
        }
      } catch (syncErr) {
        console.warn('[UnifiedStorage] Sync workflow_data to projectStore failed:', syncErr);
      }
    } catch (e) {
      console.error('[UnifiedStorage] Backend save failed:', e);
    }
  }
}

export const unifiedStorage = new UnifiedStorage();
